// In-app debug log — on-device replacement for browser devtools on
// the installed iOS PWA where we have no other way to see what's
// happening. Captures:
//   - console.log / info / warn / error / debug calls
//   - window.onerror uncaught exceptions
//   - unhandled promise rejections
//
// Storage strategy: a ring buffer of the most-recent ENTRIES_CAP
// entries, persisted to localStorage on every write. The persist is
// critical for the white-screen scenario — even if React fails to
// mount, the entries from before the failure survive a reload and
// can be inspected on the next successful boot.
//
// Designed so installPatches() is idempotent and side-effect-free
// when toggled OFF (the toggle just stops emitting new entries; the
// patched console methods stay in place for the lifetime of the
// tab so we don't tear them down mid-render).

const ENTRIES_KEY = 'phoenix.debug.entries'
const ENABLED_KEY = 'phoenix.debug.enabled'
const ENTRIES_CAP = 500
const EVENT = 'phoenix:debug-log'

let buffer = []
let patched = false

function safeRead() {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(ENTRIES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch { return [] }
}

function safePersist() {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ENTRIES_KEY, JSON.stringify(buffer))
  } catch {
    // Quota exceeded — drop the oldest half and retry once. If that
    // still fails, give up; we'd rather miss logs than crash the app.
    try {
      buffer = buffer.slice(-Math.floor(ENTRIES_CAP / 2))
      window.localStorage.setItem(ENTRIES_KEY, JSON.stringify(buffer))
    } catch { /* swallow */ }
  }
}

function stringifyArg(arg) {
  if (arg === null) return 'null'
  if (arg === undefined) return 'undefined'
  if (typeof arg === 'string') return arg
  if (arg instanceof Error) {
    return `${arg.name || 'Error'}: ${arg.message || ''}${arg.stack ? '\n' + arg.stack : ''}`
  }
  if (typeof arg === 'object') {
    try { return JSON.stringify(arg) } catch { return '[Circular]' }
  }
  return String(arg)
}

function push(level, args) {
  if (typeof window === 'undefined') return
  // Skip our own dispatcher messages if anything we log accidentally
  // contains the marker — defensive against future loops.
  const text = args.map(stringifyArg).join(' ').slice(0, 4000)
  buffer.push({ ts: Date.now(), level, text })
  if (buffer.length > ENTRIES_CAP) buffer = buffer.slice(-ENTRIES_CAP)
  safePersist()
  try {
    window.dispatchEvent(new CustomEvent(EVENT))
  } catch { /* private mode / dispatch unavailable */ }
}

// Idempotently install the console patches + window-level error
// listeners. Safe to call from app boot before React mounts so the
// boot-time errors that cause white screens still land in the log.
export function installPatches() {
  if (patched || typeof window === 'undefined') return
  patched = true
  buffer = safeRead()

  for (const level of ['log', 'info', 'warn', 'error', 'debug']) {
    const original = window.console[level]
    if (typeof original !== 'function') continue
    window.console[level] = function patched(...args) {
      try { push(level, args) } catch { /* never block the original log */ }
      try { original.apply(window.console, args) } catch { /* noop */ }
    }
  }

  window.addEventListener('error', (event) => {
    push('error', [
      `[uncaught] ${event?.message || event?.error?.message || 'unknown'}`,
      event?.filename ? `at ${event.filename}:${event.lineno}:${event.colno}` : '',
      event?.error?.stack || '',
    ].filter(Boolean))
  })

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event?.reason
    push('error', [`[unhandled] ${reason?.message || reason || 'rejected'}`, reason?.stack || ''])
  })

  // Anchor every boot so the timeline shows when the app started — a
  // common "what happened?" question across white-screen reports.
  push('info', [`[boot] phoenix start @ ${new Date().toISOString()}`])

  // Performance instrumentation. Two observers — `longtask` for
  // Chromium / Firefox, and `event` (Event Timing API) for Safari
  // which refuses to implement longtask. Anything over the threshold
  // gets logged as a [perf] line, lets the on-device debug overlay
  // tell us what's freezing without React DevTools.
  if (typeof window !== 'undefined' && typeof window.PerformanceObserver === 'function') {
    try {
      const obs = new window.PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry || (entry.duration || 0) < 100) continue
          push('warn', [`[perf] long task ${Math.round(entry.duration)}ms`])
        }
      })
      obs.observe({ entryTypes: ['longtask'] })
    } catch { /* longtask not supported (all Safari builds) */ }

    // Event Timing API — Safari + Chromium both support this. Tracks
    // user-interaction events (clicks, taps, keypresses) and reports
    // how long they took from input to next paint. A 1-second tap-
    // back-from-article that "feels frozen" surfaces here as a slow
    // "click" or "pointerdown" event with the actual processing time.
    try {
      const evObs = new window.PerformanceObserver((list) => {
        for (const entry of list.getEntries()) {
          if (!entry || (entry.duration || 0) < 200) continue
          push('warn', [`[perf] slow event ${entry.name} ${Math.round(entry.duration)}ms`])
        }
      })
      // durationThreshold filters at the browser level so we don't
      // pull thousands of fast-event entries through user space.
      evObs.observe({ type: 'event', buffered: false, durationThreshold: 200 })
    } catch { /* event timing not supported */ }
  }
}

export function getEntries() {
  return buffer.slice()
}

export function clearEntries() {
  buffer = []
  safePersist()
  if (typeof window === 'undefined') return
  try { window.dispatchEvent(new CustomEvent(EVENT)) } catch { /* noop */ }
}

export function subscribe(listener) {
  if (typeof window === 'undefined') return () => {}
  const handler = () => listener(getEntries())
  window.addEventListener(EVENT, handler)
  return () => window.removeEventListener(EVENT, handler)
}

export function isEnabled() {
  if (typeof window === 'undefined') return false
  try { return window.localStorage.getItem(ENABLED_KEY) === '1' } catch { return false }
}

export function setEnabled(on) {
  if (typeof window === 'undefined') return
  try {
    if (on) window.localStorage.setItem(ENABLED_KEY, '1')
    else window.localStorage.removeItem(ENABLED_KEY)
  } catch { /* noop */ }
}

// Format entries as a single plain-text block for clipboard sharing.
// One line per entry, ISO timestamp + level + message. Multi-line
// messages (stack traces) are preserved.
export function formatForClipboard(entries) {
  return entries.map((e) => {
    const ts = new Date(e.ts).toISOString()
    return `[${ts}] [${e.level.toUpperCase()}] ${e.text}`
  }).join('\n')
}
